import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { PromotionDraft } from '../../queries/promotions';

interface PromotionFormDialogProps {
  draft: PromotionDraft;
  onChange: (draft: PromotionDraft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';

export function PromotionFormDialog({ draft, onChange, onCancel, onSave, saving, error }: PromotionFormDialogProps) {
  const isEdit = !!draft.id;

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{isEdit ? 'Edit promotion' : 'Add promotion'}</Dialog.Title>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="text-sm">
              Source
              <input
                className={`${inputClass} font-mono`}
                value={draft.source ?? ''}
                onChange={(e) => onChange({ ...draft, source: e.target.value })}
                disabled={isEdit}
                placeholder="summer2026"
              />
              {isEdit && <p className="mt-1 text-xs text-muted-foreground">Cannot be changed after creation.</p>}
            </label>
            <label className="text-sm">
              Title
              <input className={inputClass} value={draft.title ?? ''} onChange={(e) => onChange({ ...draft, title: e.target.value })} />
            </label>
            <label className="col-span-2 text-sm">
              Description
              <input
                className={inputClass}
                value={draft.description ?? ''}
                onChange={(e) => onChange({ ...draft, description: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Discount code
              <input
                className={`${inputClass} font-mono`}
                value={draft.discount_code ?? ''}
                onChange={(e) => onChange({ ...draft, discount_code: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Package code (optional)
              <input
                className={`${inputClass} font-mono`}
                value={draft.package_code ?? ''}
                onChange={(e) => onChange({ ...draft, package_code: e.target.value || null })}
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                Promotions don't give or advertise a free trial: that comes only from the package with Auto-assign on Registration.
              </span>
            </label>
            <label className="col-span-2 text-sm">
              Image URL (optional)
              <input
                className={inputClass}
                value={draft.image_url ?? ''}
                onChange={(e) => onChange({ ...draft, image_url: e.target.value || null })}
                placeholder="https://…"
              />
            </label>
            {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
